import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import SelectField from '../../../../components/formComponents/selectField.js';
import InputField from '../../../../components/formComponents/inputField.js';
import { useToast, Button, Flex, Text } from '@chakra-ui/react';
import CompaniesApi from '../../../../api/companies';
import { useAuth } from '../../../../auth-context/auth.context';
import Card from 'components/card/Card';
import PinCodeApi from 'api/pincode';
import UpperCaseChange from 'components/formComponents/upperCaseChange.js';
import { useHistory } from 'react-router-dom';
import { CreateCompanyFormValidationSchema } from 'formValidations/createCompanyFormValidations.js';

export default function AddNewCompanyForm(props) {
  const history = useHistory();
  const { user } = useAuth();
  const toast = useToast();
  const toastIdRef = React.useRef();
  const initialRef = React.useRef(null);
  const finalRef = React.useRef(null);
  const [inpDisable, setInpDisable] = useState(true);

  const initialValues = {
    name: '',
    companyCode: '',
    email: '',
    phoneNumber: '',
    countryCode: 91,
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    landmarks: '',
    pincode: '',
    panNumber: '',
    gstNumber: '',
    status: 'ACTIVE',
  };

  const handleSubmit = async (values, actions) => {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
    }

    try {
      let response = await CompaniesApi.Create(values);
      response = response.data;
      if (response.status_code === 200) {
        toastIdRef.current = toast({
          title: response.message || 'Successfully Added',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        actions.resetForm();
        history.push('/admin/companies');
      } else {
        toastIdRef.current = toast({
          title: response.message || 'Error Adding',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      toastIdRef.current = toast({
        title: 'Unable to Add',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.log(err);
    }
  };

  const GetPincode = async (e, setFieldValue) => {
    console.log('GetPincode called', e);

    try {
      let response = await PinCodeApi.Get({ pincode: e });
      response = response.data;
      console.log(response);
      if (
        response.status_code === 200 &&
        Object.keys(response.data).length !== 0
      ) {
        setFieldValue('country', response.data.country);
        setFieldValue('state', response.data.state);
        setFieldValue('city', response.data.city);
        setInpDisable(false);
      } else {
        toastIdRef.current = toast({
          title:
            'Pincode not found! Please recheck pincode or fill City, State and Country manually.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setInpDisable(false);
      }
    } catch (error) {
      console.log('Here is Error', error);
      setInpDisable(false);
    }
  };

  return (
    <Flex
      direction='column'
      mt={20}
      mx={5}
      maxWidth={'5xl'}>
      <Formik
        initialValues={initialValues}
        validationSchema={CreateCompanyFormValidationSchema}
        onSubmit={handleSubmit}>
        {({ setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Flex direction='column'>
              <Card
                bgColor='gray.200'
                mt={12}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Name of the Company'
                    required={true}
                    name='name'
                    type='text'
                  />
                  <Flex>
                    <InputField
                      label='Company Code'
                      required={true}
                      name='companyCode'
                      type='text'
                      width='xs'
                      onChange={UpperCaseChange}
                    />
                  </Flex>
                </Flex>

                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='E-Mail Address'
                    required={true}
                    name='email'
                    type='email'
                  />
                  <InputField
                    label='Phone Number'
                    required={true}
                    name='phoneNumber'
                    type='number'
                  />
                  <SelectField
                    label='Status'
                    name='status'
                    options={[
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'INACTIVE', label: 'Inactive' },
                    ]}
                  />
                </Flex>
              </Card>

              <Text
                mt={12}
                fontSize='xl'
                fontWeight='700'>
                Postal Address :
              </Text>

              <Card
                bgColor='gray.200'
                mt={4}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Address Line 1'
                    required={true}
                    name='addressLine1'
                    type='text'
                  />
                  <InputField
                    label='Address Line 2'
                    name='addressLine2'
                    type='text'
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Landmarks'
                    required={true}
                    name='landmarks'
                    type='text'
                  />
                  <InputField
                    label='Pincode'
                    required={true}
                    name='pincode'
                    type='number'
                    onChange={(e) => {
                      setFieldValue('pincode', e.target.value);
                      e.target.value.length === 6 &&
                        GetPincode(e.target.value, setFieldValue);
                    }}
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='City'
                    required={true}
                    name='city'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable ? 'gray.200' : ''}
                    disabled={inpDisable}
                  />
                  <InputField
                    label='State'
                    required={true}
                    name='state'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable ? 'gray.200' : ''}
                    disabled={inpDisable}
                  />
                  <InputField
                    label='Country'
                    required={true}
                    name='country'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable ? 'gray.200' : ''}
                    disabled={inpDisable}
                  />
                </Flex>
              </Card>

              <Text
                mt={12}
                fontSize='xl'
                fontWeight='700'>
                Other Details :
              </Text>

              <Card
                bgColor='gray.200'
                mt={4}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='PAN Number'
                    required={true}
                    name='panNumber'
                    type='text'
                    placeholder='10 Character PAN Number'
                    onChange={UpperCaseChange}
                    maxLength='10'
                  />
                  <InputField
                    label='GST Number'
                    required={true}
                    name='gstNumber'
                    type='text'
                    placeholder='15 Character GST Number'
                    onChange={UpperCaseChange}
                    maxLength='15'
                  />
                </Flex>
              </Card>

              <Flex>
                <Button
                  mt={20}
                  colorScheme='blue'
                  type='submit'
                  disabled={isSubmitting}>
                  Add New Company
                </Button>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
}
